import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Table, Modal } from 'react-bootstrap';
import axios from 'axios';

const AdminWaiters = () => {
  const [form, setForm] = useState({ name: '', phone: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Waiters list
  const [waiters, setWaiters] = useState([]);
  const [loadingWaiters, setLoadingWaiters] = useState(false);
  const [waitersError, setWaitersError] = useState('');
  const [managerProfile, setManagerProfile] = useState(null);

  // Delete waiter modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [waiterToDelete, setWaiterToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const onChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const fetchManagerProfile = async () => {
    try {
      // Try both restaurant token and manager token
      const restaurantToken = localStorage.getItem('restaurantToken');
      const managerToken = localStorage.getItem('managerToken');
      const token = restaurantToken || managerToken;
      
      if (!token) return;

      // For restaurant owners, we need to get profile differently
      if (restaurantToken) {
        // Restaurant owner profile
        const res = await axios.get('/api/auth/profile', {
          headers: { 'x-auth-token': restaurantToken }
        });
        setManagerProfile({ ...res.data, role: 'restaurant' });
      } else {
        // Manager profile
        const res = await axios.get('/api/manager/profile', {
          headers: { 'x-auth-token': managerToken }
        });
        setManagerProfile(res.data);
      }
    } catch (err) {
      console.error('Error fetching manager profile:', err);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const restaurantToken = localStorage.getItem('restaurantToken');
      const managerToken = localStorage.getItem('managerToken');
      const token = restaurantToken || managerToken;
      
      if (!token) {
        setError('Вы не авторизованы как администратор или директор заведения.');
        setLoading(false);
        return;
      }

      // Use different endpoint based on user type
      if (restaurantToken) {
        // Restaurant owner creates waiter via register endpoint
        await axios.post('/api/manager/register', { ...form, role: 'waiter' }, {
          headers: { 'x-auth-token': token }
        });
      } else {
        // Manager creates waiter via create-waiter endpoint
        await axios.post('/api/manager/create-waiter', form, {
          headers: { 'x-auth-token': token }
        });
      }

      setSuccess('Официант успешно создан.');
      setForm({ name: '', phone: '', password: '' });
      // reload list
      await loadWaiters();
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Не удалось создать официанта';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const loadWaiters = async () => {
    try {
      setWaitersError('');
      setLoadingWaiters(true);
      const restaurantToken = localStorage.getItem('restaurantToken');
      const managerToken = localStorage.getItem('managerToken');
      const token = restaurantToken || managerToken;
      
      if (!token) {
        setWaitersError('Вы не авторизованы как администратор или директор заведения.');
        setLoadingWaiters(false);
        return;
      }
      // Use different endpoint based on user type
      let res;
      if (restaurantToken) {
        // Restaurant owner gets waiters via managers list endpoint
        res = await axios.get('/api/manager', {
          headers: { 'x-auth-token': token }
        });
        // Filter only waiters from the response
        const waiters = res.data.filter(manager => manager.role === 'waiter');
        setWaiters(waiters);
      } else {
        // Manager gets waiters via waiters endpoint
        res = await axios.get('/api/manager/waiters', {
          headers: { 'x-auth-token': token }
        });
        setWaiters(res.data || []);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Не удалось загрузить список официантов';
      setWaitersError(msg);
    } finally {
      setLoadingWaiters(false);
    }
  };

  const handleDeleteClick = (waiter) => {
    setWaiterToDelete(waiter);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!waiterToDelete) return;
    
    setDeleting(true);
    try {
      const restaurantToken = localStorage.getItem('restaurantToken');
      const managerToken = localStorage.getItem('managerToken');
      const token = restaurantToken || managerToken;
      // Use different endpoint based on user type
      if (restaurantToken) {
        // Restaurant owner deletes waiter via manager delete endpoint
        await axios.delete(`/api/manager/${waiterToDelete._id}`, {
          headers: { 'x-auth-token': token }
        });
      } else {
        // Manager deletes waiter via waiter delete endpoint
        await axios.delete(`/api/manager/waiter/${waiterToDelete._id}`, {
          headers: { 'x-auth-token': token }
        });
      }
      
      setShowDeleteModal(false);
      setWaiterToDelete(null);
      await loadWaiters();
    } catch (err) {
      console.error('Error deleting waiter:', err);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    fetchManagerProfile();
    loadWaiters();
  }, []);

  return (
    <Container className="mt-5">
      {managerProfile?.role !== 'administrator' && managerProfile?.role !== 'restaurant' ? (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger">
              <h4>Доступ запрещен</h4>
              <p>Только администраторы и директора заведений могут создавать официантов.</p>
            </Alert>
          </Col>
        </Row>
      ) : (
        <>
          <Row className="mb-4">
            <Col>
              <h1>Управление официантами</h1>
              <p>Создавайте учетные записи официантов для обработки заказов.</p>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <Card className="mb-4">
                <Card.Body>
                  <Card.Title>Добавить официанта</Card.Title>
                  {success && <Alert variant="success">{success}</Alert>}
                  {error && <Alert variant="danger">{error}</Alert>}
                  <Form onSubmit={onSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label>Имя официанта (необязательно)</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={onChange}
                        placeholder="Например: Иван Петров"
                      />
                      <Form.Text className="text-muted">
                        Если не указано, будет использовано "Официант + номер телефона"
                      </Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Номер телефона</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={onChange}
                        placeholder="+375291234567"
                        required
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Пароль</Form.Label>
                      <Form.Control
                        type="password"
                        name="password"
                        value={form.password}
                        onChange={onChange}
                        placeholder="Минимум 6 символов"
                        required
                      />
                    </Form.Group>
                    <Button type="submit" variant="primary" disabled={loading}>
                      {loading ? 'Создание...' : 'Создать официанта'}
                    </Button>
                  </Form>
                  <hr />
                  <p className="mb-0">
                    После создания официант может войти в мобильное приложение и обрабатывать заказы.
                  </p>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card>
                <Card.Body>
                  <Card.Title>Список официантов</Card.Title>
                  {waitersError && <Alert variant="danger">{waitersError}</Alert>}
                  {loadingWaiters ? (
                    <div className="text-muted">Загрузка списка...</div>
                  ) : waiters.length === 0 ? (
                    <div className="text-muted">Официанты не найдены</div>
                  ) : (
                    <Table bordered hover responsive>
                      <thead>
                        <tr>
                          <th>Имя</th>
                          <th>Номер телефона</th>
                          <th>Создан</th>
                          <th>Действия</th>
                        </tr>
                      </thead>
                      <tbody>
                        {waiters.map((w) => (
                          <tr key={w._id}>
                            <td>
                              {w.name && !w.name.startsWith('Официант ') ? w.name : '—'}
                            </td>
                            <td>{w.phone}</td>
                            <td>{new Date(w.createdAt).toLocaleString()}</td>
                            <td>
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleDeleteClick(w)}
                              >
                                Удалить
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Подтверждение удаления</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Вы уверены, что хотите удалить официанта "{waiterToDelete?.name || waiterToDelete?.phone}"?
          Это действие нельзя отменить.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Отмена
          </Button>
          <Button variant="danger" onClick={handleDeleteConfirm} disabled={deleting}>
            {deleting ? 'Удаление...' : 'Удалить'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminWaiters;
